import type { ParsedHoursYext, YextDayHours } from '@/types/center';
import { DEFAULT_TIMEZONE, isSupportedTimeZone } from '@/utils/timezone';

type YextDayKey = keyof ParsedHoursYext;

interface Center {
  name: string;
  timezone: string;
  hours: {
    officeHoursYext?: string;
  } | null;
}

/**
 * Radius stores Windows zone ids and guardian-portal-api translates them, so an unusable value here
 * means that translation was bypassed. `Intl` throws on one, which would take the whole home heading
 * down — opening hours belong to the center, so fall back to the default rather than the device.
 */
const resolveCenterTimezone = (timezone: string | undefined): string => (timezone && isSupportedTimeZone(timezone) ? timezone : DEFAULT_TIMEZONE);

const yextDayKeys: YextDayKey[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const getYextDayKeyInTimezone = (timezone: string, referenceDate = new Date()): YextDayKey => {
  const dayIndex = new Date(referenceDate.toLocaleString('en-US', { timeZone: timezone })).getDay();
  return yextDayKeys[dayIndex];
};

const getTomorrowYextDayKeyFrom = (todayKey: YextDayKey): YextDayKey => {
  const i = yextDayKeys.indexOf(todayKey);
  return yextDayKeys[(i + 1) % 7];
};

const hhmmToMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

const formatLocalTime = (hhmm: string): string => {
  const [hStr, mStr] = hhmm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${m.toString().padStart(2, '0')} ${suffix}`;
};

const getNowMinutesInTimezone = (timezone: string, referenceDate = new Date()): number => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).formatToParts(referenceDate);
  const h = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const m = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return h * 60 + m;
};

const isNowInside = (nowMin: number, startMin: number, endMin: number): boolean => {
  if (startMin === endMin) return false;
  if (endMin > startMin) return nowMin >= startMin && nowMin < endMin;
  return nowMin >= startMin || nowMin < endMin;
};

const parseOfficeHoursYext = (center: Center): ParsedHoursYext | null => {
  if (!center.hours?.officeHoursYext) return null;
  try {
    return JSON.parse(center.hours.officeHoursYext) as ParsedHoursYext;
  } catch {
    return null;
  }
};

const getDayHours = (parsedHours: ParsedHoursYext | null, dayKey: YextDayKey): YextDayHours | null => {
  if (!parsedHours) return null;
  return parsedHours[dayKey] || null;
};

interface OpenTodayResult {
  timezone: string;
  dayKey: YextDayKey;
  dayHours: YextDayHours | null;
  isOpenNow: boolean;
  isOpenSoonToday: boolean;
  formattedLabel: string | null;
}

export const computeOpenToday = (center?: Center, referenceDate = new Date()): OpenTodayResult => {
  if (!center) {
    return {
      timezone: DEFAULT_TIMEZONE,
      dayKey: 'monday',
      dayHours: null,
      isOpenNow: false,
      isOpenSoonToday: false,
      formattedLabel: null,
    };
  }

  const timezone = resolveCenterTimezone(center.timezone);
  const parsedHours = parseOfficeHoursYext(center);
  const dayKey = getYextDayKeyInTimezone(timezone, referenceDate);
  const dayHours = getDayHours(parsedHours, dayKey);

  // If no hours data or day is closed, return closed state
  if (!dayHours || dayHours.isClosed || !dayHours.openIntervals) {
    return {
      timezone,
      dayKey,
      dayHours,
      isOpenNow: false,
      isOpenSoonToday: false,
      formattedLabel: null,
    };
  }

  const { start, end } = dayHours.openIntervals;
  const nowMin = getNowMinutesInTimezone(timezone, referenceDate);
  const startMin = hhmmToMinutes(start);
  const endMin = hhmmToMinutes(end);

  const isOpenNow = isNowInside(nowMin, startMin, endMin);
  const isOpenSoonToday = !isOpenNow && nowMin < startMin;

  const formattedLabel = `${formatLocalTime(start)} - ${formatLocalTime(end)}`;

  return { timezone, dayKey, dayHours, isOpenNow, isOpenSoonToday, formattedLabel };
};

interface OpenTomorrowResult {
  timezone: string;
  dayKey: YextDayKey;
  dayHours: YextDayHours | null;
  isOpenTomorrow: boolean;
  formattedLabel: string | null;
}

export const computeOpenTomorrow = (center?: Center, referenceDate = new Date()): OpenTomorrowResult => {
  if (!center) {
    return {
      timezone: DEFAULT_TIMEZONE,
      dayKey: 'monday',
      dayHours: null,
      isOpenTomorrow: false,
      formattedLabel: null,
    };
  }

  const timezone = resolveCenterTimezone(center.timezone);
  const parsedHours = parseOfficeHoursYext(center);
  const todayKey = getYextDayKeyInTimezone(timezone, referenceDate);
  const dayKey = getTomorrowYextDayKeyFrom(todayKey);
  const dayHours = getDayHours(parsedHours, dayKey);

  // If no hours data or day is closed, return closed state
  if (!dayHours || dayHours.isClosed || !dayHours.openIntervals) {
    return {
      timezone,
      dayKey,
      dayHours,
      isOpenTomorrow: false,
      formattedLabel: null,
    };
  }

  const { start, end } = dayHours.openIntervals;
  const formattedLabel = `${formatLocalTime(start)} - ${formatLocalTime(end)}`;

  return { timezone, dayKey, dayHours, isOpenTomorrow: true, formattedLabel };
};
